import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
  Info as InfoIcon,
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import XLSX from "xlsx-js-style";
import moment from "moment";
import toast from "react-hot-toast";
import "moment/locale/pt";

import { useLoading } from "../../../../../shared/context/LoadingContext";
import { useHttpClient } from "../../../../../shared/hooks/http-hook";
import MultiSelectRelatorio from "../../ui/filtrosPesquisa/MultiSelectRelatorio";
import SelectTriEstado from "../../ui/filtrosPesquisa/SelectTriEstado";
import "../../ui/filtrosPesquisa/FiltrosPesquisa.scss";

moment.locale("pt");

const FILTROS_VAZIOS = {
  criadoPor: [],
  medicoResponsavel: [],
  estabelecimento: [],
  nomeCliente: "",
  aprovado: "",
  // Só quem está sem voltar há pelo menos tantos dias. "" = todos.
  diasMinimos: "",
};

/* Quem saiu com o orçamento anteontem ainda está a pensar. Os cortes são os
   que a clínica usa ao telefone: duas semanas, um mês, três meses. */
const OPCOES_ESPERA = [
  { valor: "", rotulo: "Qualquer tempo" },
  { valor: "15", rotulo: "Há mais de 15 dias" },
  { valor: "30", rotulo: "Há mais de 1 mês" },
  { valor: "90", rotulo: "Há mais de 3 meses" },
  { valor: "180", rotulo: "Há mais de 6 meses" },
];

const CartaoResumo = ({ titulo, valor, nota, cor = "primary.main", icone }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent sx={{ py: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
        <Typography variant="body2" color="textSecondary">
          {titulo}
        </Typography>
        {icone}
      </Box>
      <Typography variant="h5" sx={{ color: cor, fontWeight: "bold" }}>
        {valor}
      </Typography>
      {nota && (
        <Typography variant="caption" color="textSecondary">
          {nota}
        </Typography>
      )}
    </CardContent>
  </Card>
);

/**
 * Clientes com orçamento que não voltaram à clínica.
 *
 * ─── PARA QUE SERVE ─────────────────────────────────────────────────────────
 *
 * É a lista de telefonemas. Cada linha é uma pessoa que levou um orçamento e,
 * desde então, não voltou nem tem nada marcado.
 *
 * Normalmente bastava olhar para os orçamentos não aprovados — não aprovado é
 * cliente que o levou e ainda não voltou. A K Dent aprovou tudo e perdeu esse
 * sinal, por isso este relatório vai buscá-lo à agenda: compara a última
 * marcação do cliente com o seu último orçamento.
 *
 * ─── UMA LINHA POR PESSOA ───────────────────────────────────────────────────
 *
 * Um cliente com três orçamentos é um telefonema, não três. A linha mostra o
 * último orçamento, que é o que a conversa vai usar, e conta os outros.
 */
function RelatorioOrcamentosSemAgendamento({
  dataFiltro,
  dadosClinica,
  auth,
  acao,
  atualizacoes,
}) {
  const { startLoading, stopLoading } = useLoading();
  const { sendRequest } = useHttpClient();
  const [dados, setDados] = useState(null);
  const [error, setError] = useState(null);
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [mostrarFiltros, setMostrarFiltros] = useState(true);
  const [procuraLocal, setProcuraLocal] = useState("");
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(25);

  const clinicaId = dadosClinica._id;
  const mudar = (campo, valor) =>
    setFiltros((prev) => ({ ...prev, [campo]: valor }));

  const moeda = (v) =>
    new Intl.NumberFormat("pt-CV", {
      style: "currency",
      currency: "CVE",
      maximumFractionDigits: 0,
    }).format(v || 0);

  const gerarRelatorio = useCallback(async () => {
    setError(null);
    startLoading();
    try {
      const body = {
        ...filtros,
        dataInicio: dataFiltro.dataInicial.format("YYYY-MM-DD"),
        dataFim: dataFiltro.dataFinal.format("YYYY-MM-DD"),
      };

      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/orcamentos/relatorio-sem-agendamento/${clinicaId}`,
        "POST",
        JSON.stringify(body),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      setDados(resposta);
      setPagina(0);
    } catch (err) {
      setError(err.message || "Erro ao gerar o relatório");
      toast.error("Erro ao gerar o relatório");
    } finally {
      stopLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicaId, dataFiltro, filtros, auth.token]);

  /* Exporta o que está no ecrã, incluindo a procura escrita na caixa: o que se
     vê é o que sai. */
  const exportarRelatorio = useCallback(() => {
    if (!dados) {
      toast.error("É necessário gerar o relatório primeiro.");
      return;
    }

    const linhas = filtrarLocalmente(dados.linhas, procuraLocal);
    const wb = XLSX.utils.book_new();

    const cabecalho = [
      "Cliente",
      "Contacto",
      "NIF",
      "Último orçamento",
      "Data",
      "Dias sem voltar",
      "Nº de orçamentos",
      "Valor",
      "Algum aprovado",
      "Última ida à clínica",
      "Médico",
      "Criado por",
      "Unidade",
    ];

    const ws = XLSX.utils.aoa_to_sheet([
      ["Clientes com orçamento que não voltaram"],
      [
        `Orçamentos de ${dataFiltro.dataInicial.format(
          "DD/MM/YYYY",
        )} a ${dataFiltro.dataFinal.format("DD/MM/YYYY")}`,
      ],
      [`Gerado a ${moment().format("DD/MM/YYYY HH:mm")}`],
      [],
      cabecalho,
      ...linhas.map((l) => [
        l.cliente?.nome || "-",
        l.cliente?.contacto || "-",
        l.cliente?.nif || "-",
        l.numero || "-",
        moment(l.dataUltimoOrcamento).format("DD/MM/YYYY"),
        l.diasDesdeOrcamento,
        l.nrOrcamentos,
        l.valor,
        l.algumAprovado ? "Sim" : "Não",
        l.ultimaMarcacao
          ? moment(l.ultimaMarcacao).format("DD/MM/YYYY")
          : "Nunca",
        l.medicoResponsavel || "-",
        l.criadoPor || "-",
        l.unidade || "-",
      ]),
    ]);

    const estiloCabecalho = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "366092" } },
      alignment: { horizontal: "center" },
    };
    cabecalho.forEach((_, i) => {
      const ref = XLSX.utils.encode_cell({ r: 4, c: i });
      if (ws[ref]) ws[ref].s = estiloCabecalho;
    });
    ws["!cols"] = cabecalho.map((c) => ({ wch: Math.max(12, c.length + 3) }));
    XLSX.utils.book_append_sheet(wb, ws, "Não voltaram");

    const r = dados.resumo;
    const wsResumo = XLSX.utils.aoa_to_sheet([
      ["RESUMO"],
      [],
      ["Clientes que não voltaram", r.total],
      ["Valor parado", r.valor],
      ["O mais antigo está à espera há (dias)", r.maisAntigoDias],
      [],
      ["Nunca tiveram marcação nenhuma", r.nunca],
      [],
      ["Com orçamento já aprovado", r.aprovados],
      ["Valor dos já aprovados", r.valorAprovados],
    ]);
    wsResumo["A1"].s = { font: { bold: true, sz: 14 } };
    wsResumo["!cols"] = [{ wch: 40 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, wsResumo, "Resumo");

    XLSX.writeFile(
      wb,
      `clientes_que_nao_voltaram_${moment().format("YYYY-MM-DD_HHmm")}.xlsx`,
    );
    toast.success("Ficheiro exportado.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados, procuraLocal, dataFiltro]);

  useEffect(() => {
    if (acao === "gerar") gerarRelatorio();
    else if (acao === "exportar") exportarRelatorio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acao, atualizacoes]);

  const opcoes = dados?.opcoes || {};
  const linhasVisiveis = useMemo(
    () => filtrarLocalmente(dados?.linhas || [], procuraLocal),
    [dados, procuraLocal],
  );
  const pagina0 = pagina * porPagina;
  const linhasPagina = linhasVisiveis.slice(pagina0, pagina0 + porPagina);

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Clientes com Orçamento que não Voltaram
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Clientes que levaram um orçamento no período e, desde então, não
              voltaram nem têm nada marcado — nem na agenda, nem em tratamento
              isolado, nem marcação provisória.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Mostrar/ocultar filtros">
              <IconButton onClick={() => setMostrarFiltros((v) => !v)}>
                <FilterListIcon />
              </IconButton>
            </Tooltip>
            {dados && (
              <Button
                startIcon={<FileDownloadIcon />}
                onClick={exportarRelatorio}
                variant="contained"
              >
                Exportar Excel
              </Button>
            )}
          </Box>
        </Box>

        {/* ── Filtros ───────────────────────────────────────────────────── */}
        <Collapse in={mostrarFiltros}>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 2 }}>
              Filtros
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <TextField
                select
                size="small"
                label="Sem voltar"
                value={filtros.diasMinimos}
                onChange={(e) => mudar("diasMinimos", e.target.value)}
                sx={{ width: 220 }}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                {OPCOES_ESPERA.map((o) => (
                  <option key={o.valor} value={o.valor}>
                    {o.rotulo}
                  </option>
                ))}
              </TextField>

              <MultiSelectRelatorio
                label="Criado por"
                value={filtros.criadoPor}
                onChange={(v) => mudar("criadoPor", v)}
                opcoes={opcoes.criadores || []}
                largura={260}
              />
              <MultiSelectRelatorio
                label="Médico responsável"
                value={filtros.medicoResponsavel}
                onChange={(v) => mudar("medicoResponsavel", v)}
                opcoes={opcoes.medicos || []}
                largura={260}
              />
              <MultiSelectRelatorio
                label="Unidade"
                value={filtros.estabelecimento}
                onChange={(v) => mudar("estabelecimento", v)}
                opcoes={opcoes.unidades || []}
                rotuloDe={(u) => `${u.codigo} — ${u.nome}`}
                largura={240}
              />

              <TextField
                size="small"
                label="Nome do cliente"
                placeholder="parte do nome"
                value={filtros.nomeCliente}
                onChange={(e) => mudar("nomeCliente", e.target.value)}
                sx={{ width: 220 }}
              />

              <SelectTriEstado
                label="Orçamento aprovado"
                value={filtros.aprovado}
                onChange={(v) => mudar("aprovado", v)}
                largura={200}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              <Button variant="contained" onClick={gerarRelatorio}>
                Aplicar filtros
              </Button>
              <Button
                onClick={() => {
                  setFiltros(FILTROS_VAZIOS);
                  setProcuraLocal("");
                }}
              >
                Limpar
              </Button>
            </Box>
          </Paper>
        </Collapse>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {dados?.truncado && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            São {dados.total} clientes e a lista mostra os primeiros{" "}
            {dados.maxLinhas}. <strong>Os totais contam todos.</strong> Para ver
            os restantes, aperte o período ou use mais filtros.
          </Alert>
        )}

        {/* ── Os números ────────────────────────────────────────────────── */}
        {dados && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <CartaoResumo
                titulo="Não voltaram"
                valor={dados.resumo.total}
                nota={
                  dados.resumo.maisAntigoDias
                    ? `o mais antigo há ${dados.resumo.maisAntigoDias} dias`
                    : "clientes para contactar"
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CartaoResumo
                titulo="Valor parado"
                valor={moeda(dados.resumo.valor)}
                nota="trabalho orçamentado e sem data"
                cor="warning.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CartaoResumo
                titulo="Já aprovados"
                valor={dados.resumo.aprovados}
                nota={moeda(dados.resumo.valorAprovados)}
                cor="error.main"
                icone={
                  <Tooltip title="Orçamento aprovado, conta aberta, e o cliente não voltou. São os primeiros telefonemas.">
                    <InfoIcon fontSize="small" color="action" />
                  </Tooltip>
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CartaoResumo
                titulo="Nunca tiveram marcação"
                valor={dados.resumo.nunca}
                nota="nem a da própria avaliação"
              />
            </Grid>
          </Grid>
        )}

        {/* ── Procura dentro do resultado ───────────────────────────────── */}
        {dados && (
          <TextField
            fullWidth
            size="small"
            placeholder="Procurar no resultado: cliente, contacto, nº, médico, criador…"
            value={procuraLocal}
            onChange={(e) => {
              setProcuraLocal(e.target.value);
              setPagina(0);
            }}
            sx={{ mb: 2 }}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
          />
        )}

        {!dados && !error && (
          <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
            Escolha o período e aperte «Gerar Relatório».
          </Typography>
        )}

        {/* ── A lista ───────────────────────────────────────────────────── */}
        {dados && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {linhasVisiveis.length} cliente(s)
              {procuraLocal ? ` de ${dados.linhas.length} na lista` : ""}
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Contacto</TableCell>
                    <TableCell>Último orçamento</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell align="right">Sem voltar</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="center">Estado</TableCell>
                    <TableCell>Última ida à clínica</TableCell>
                    <TableCell>Médico</TableCell>
                    <TableCell>Criado por</TableCell>
                    <TableCell>Unidade</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linhasPagina.map((l) => (
                    <TableRow key={l._id} hover>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <MuiLink
                            component={Link}
                            to={l.cliente.link}
                            color="inherit"
                            underline="hover"
                          >
                            {l.cliente.nome}
                          </MuiLink>
                          <Tooltip title="Abrir o orçamento do cliente">
                            <IconButton
                              size="small"
                              component={Link}
                              to={l.cliente.link}
                            >
                              <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {l.cliente?.contacto || "-"}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {l.numero || "-"}
                        {l.nrOrcamentos > 1 && (
                          <Tooltip
                            title={`Este cliente tem ${l.nrOrcamentos} orçamentos no período. O valor é a soma de todos.`}
                          >
                            <Chip
                              size="small"
                              label={`+${l.nrOrcamentos - 1}`}
                              sx={{ ml: 0.5 }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {moment(l.dataUltimoOrcamento).format("DD/MM/YYYY")}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          whiteSpace: "nowrap",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {l.diasDesdeOrcamento} d
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {moeda(l.valor)}
                      </TableCell>
                      <TableCell align="center">
                        {l.algumAprovado ? (
                          <Tooltip title="Aprovado, com conta aberta, e o cliente não voltou">
                            <Chip
                              size="small"
                              label="Aprovado"
                              color="error"
                              variant="outlined"
                            />
                          </Tooltip>
                        ) : (
                          <Chip
                            size="small"
                            label="Não aprovado"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {l.ultimaMarcacao ? (
                          <>
                            {moment(l.ultimaMarcacao).format("DD/MM/YYYY")}{" "}
                            <Typography
                              variant="caption"
                              color="textSecondary"
                            >
                              (há {l.diasDesdeUltimaMarcacao} d)
                            </Typography>
                          </>
                        ) : (
                          <Chip size="small" label="Nunca" color="warning" />
                        )}
                      </TableCell>
                      <TableCell>{l.medicoResponsavel || "-"}</TableCell>
                      <TableCell>{l.criadoPor || "-"}</TableCell>
                      <TableCell>{l.unidade || "-"}</TableCell>
                    </TableRow>
                  ))}

                  {linhasVisiveis.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ py: 3 }}
                        >
                          Nenhum cliente nestas condições. Todos os que
                          receberam orçamento no período voltaram ou têm
                          marcação.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              count={linhasVisiveis.length}
              page={pagina}
              onPageChange={(e, nova) => setPagina(nova)}
              rowsPerPage={porPagina}
              onRowsPerPageChange={(e) => {
                setPorPagina(parseInt(e.target.value, 10));
                setPagina(0);
              }}
              labelRowsPerPage="Registos por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
            />
          </>
        )}
      </Paper>
    </Box>
  );
}

/** Procura em tudo o que a linha mostra. */
function filtrarLocalmente(linhas, termo) {
  const t = String(termo || "")
    .trim()
    .toLowerCase();
  if (!t) return linhas;

  return linhas.filter((l) =>
    [
      l.numero,
      l.cliente?.nome,
      l.cliente?.contacto,
      l.cliente?.nif,
      l.medicoResponsavel,
      l.criadoPor,
      l.unidade,
    ]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(t)),
  );
}

export default RelatorioOrcamentosSemAgendamento;
